import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { EmptyState } from "./EmptyState";
import { BellRing, Trash2 } from "lucide-react";

const NONE = "__none__";

export const AlertsTab = () => {
  const [meta, setMeta] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [f, setF] = useState({ district: "", category: "", keyword: "", regime: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/candidate/alerts").then((r) => setAlerts(r.data.alerts)).catch(() => {});
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!f.district || !f.category) { toast.error("Escolhe distrito e categoria"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/candidate/alerts", {
        district: f.district, category: f.category,
        keyword: f.keyword || null, regime: f.regime || null,
      });
      setAlerts([...alerts, data]);
      setF({ district: "", category: "", keyword: "", regime: "" });
      toast.success("Alerta criado — vais receber email quando entrar uma vaga compatível");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/candidate/alerts/${id}`);
      setAlerts(alerts.filter((a) => a.id !== id));
      toast.success("Alerta removido");
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <form onSubmit={create} className="bg-white rounded-2xl border border-black/5 p-6 space-y-4">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1.5">
          <BellRing className="w-3.5 h-3.5 text-amber-500" /> Novo alerta de vagas
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block">Distrito *</Label>
            <Select value={f.district} onValueChange={(v) => setF({ ...f, district: v })}>
              <SelectTrigger data-testid="alert-district-select"><SelectValue placeholder="Seleciona..." /></SelectTrigger>
              <SelectContent>{Object.keys(meta?.districts || {}).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block">Categoria *</Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="alert-category-select"><SelectValue placeholder="Seleciona..." /></SelectTrigger>
              <SelectContent>{(meta?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block">Palavra-chave (opcional)</Label>
            <Input data-testid="alert-keyword-input" value={f.keyword} onChange={(e) => setF({ ...f, keyword: e.target.value })} placeholder="Ex.: cozinheiro" />
          </div>
          <div>
            <Label className="mb-1.5 block">Regime (opcional)</Label>
            <Select value={f.regime || NONE} onValueChange={(v) => setF({ ...f, regime: v === NONE ? "" : v })}>
              <SelectTrigger data-testid="alert-regime-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Qualquer</SelectItem>
                {(meta?.regimes || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button data-testid="alert-create-btn" type="submit" disabled={saving} className="rounded-full bg-slate-900 hover:bg-slate-800">
          {saving ? "A criar..." : "Criar alerta"}
        </Button>
      </form>

      {alerts.length === 0 ? (
        <EmptyState icon={BellRing} title="Sem alertas ativos" description="Cria um alerta e recebe email assim que entrar uma vaga compatível." />
      ) : (
        <div className="space-y-3">
          {alerts.map((a) => (
            <div key={a.id} data-testid={`alert-row-${a.id}`} className="bg-white rounded-xl border border-black/5 p-4 flex items-center justify-between gap-3">
              <div className="text-sm">
                <p className="font-semibold text-slate-900">{a.category} · {a.district}</p>
                <p className="text-xs text-slate-500">
                  {[a.keyword && `"${a.keyword}"`, a.regime].filter(Boolean).join(" · ") || "Sem filtros extra"}
                  {" · email imediato"}
                </p>
              </div>
              <Button data-testid={`alert-delete-${a.id}`} size="sm" variant="ghost" className="rounded-full text-red-600 hover:text-red-700" onClick={() => remove(a.id)}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
